import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Modal } from '../ui/Modal';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import {
  DEFAULT_SCOPE_COLOR,
  DEFAULT_SCOPE_ICON,
  SCOPE_COLORS,
  SCOPE_ICONS,
  SCOPE_NAME_MAX,
} from '../../extensions/bundled/documents/documentsScopes';
import { setUpgradeModalOpen, setUpgradeReason } from '../../store/uiModalStore';
import WorkspaceChip from './WorkspaceChip';
import { workspaceGlyph } from './workspaceIcons';
import './workspaces.css';

const SECONDARY = 'px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm';
const PRIMARY =
  'px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm';

/**
 * New workspace / Edit workspace (card 5.2): a modal-input name, the icon as a
 * CompactSelectPicker (glyph + name, searchable) and the eight brand colours as
 * swatches; a live chip in the header previews the result. `initial` = edit.
 *
 * `atLimit` (the plan's synced-workspace count is reached): creating still
 * works — the workspace stays on this device, like a document over the cloud
 * limit — and the modal says so with the standard upgrade link.
 */
export default function WorkspaceModal({ initial = null, atLimit = false, onSubmit, onClose, ns = 'docSidebar.workspaces' }) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name || '');
  const [icon, setIcon] = useState(initial?.icon || DEFAULT_SCOPE_ICON);
  const [color, setColor] = useState(initial?.color || DEFAULT_SCOPE_COLOR);
  const [busy, setBusy] = useState(false);
  const editing = initial != null;

  const iconOptions = useMemo(
    () =>
      SCOPE_ICONS.map((key) => {
        const Glyph = workspaceGlyph(key);
        return { value: key, label: t(`docSidebar.workspaces.icons.${key}`), leading: <Glyph size={14} strokeWidth={1.8} /> };
      }),
    [t]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    const ok = await onSubmit({ name: name.trim(), icon, color });
    if (!ok) setBusy(false);
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={editing ? t(`${ns}.editTitle`) : t(`${ns}.newTitle`)}
      subtitle={editing ? '' : t(`${ns}.newSubtitle`)}
      customIcon={<WorkspaceChip choice={{ kind: 'workspace', icon, color }} size={28} />}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="doc-workspace-modal">
        <label className="doc-ws-field">
          <span className="doc-ws-label">{t(`${ns}.nameLabel`)}</span>
          <input
            type="text"
            className="modal-input"
            value={name}
            maxLength={SCOPE_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(`${ns}.namePlaceholder`)}
            aria-label={t(`${ns}.nameLabel`)}
            autoFocus
          />
        </label>
        <div className="doc-ws-field">
          <span className="doc-ws-label">{t(`${ns}.iconLabel`)}</span>
          <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
            <CompactSelectPicker
              value={icon}
              onChange={setIcon}
              options={iconOptions}
              searchable
              searchPlaceholder={t(`${ns}.iconSearch`)}
              menuMinWidth={220}
              aria-label={t(`${ns}.iconLabel`)}
            />
          </div>
        </div>
        <div className="doc-ws-field">
          <span className="doc-ws-label" id="doc-ws-color-label">
            {t(`${ns}.colorLabel`)}
          </span>
          <div className="doc-ws-swatches" role="radiogroup" aria-labelledby="doc-ws-color-label">
            {SCOPE_COLORS.map((hex, i) => (
              <button
                key={hex}
                type="button"
                role="radio"
                aria-checked={color === hex}
                aria-label={t(`${ns}.colorN`, { n: i + 1 })}
                className="doc-ws-swatch"
                style={{ background: hex }}
                onClick={() => setColor(hex)}
              >
                {color === hex && <Check size={12} strokeWidth={2.5} aria-hidden />}
              </button>
            ))}
          </div>
        </div>
        {atLimit && !editing && (
          <p className="doc-ws-limit" data-testid="doc-workspace-limit">
            {t(`${ns}.limit`)}{' '}
            <button
              type="button"
              className="doc-ws-upgrade"
              onClick={() => {
                setUpgradeReason(t(`${ns}.upgradeReason`));
                setUpgradeModalOpen(true);
              }}
            >
              {t('planLimits.upgrade')}
            </button>
          </p>
        )}
        <div className="flex justify-end space-x-2 pt-2">
          <button type="button" className={SECONDARY} onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className={PRIMARY} disabled={!name.trim() || busy}>
            {editing ? t('common.save') : t(`${ns}.create`)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
